import { CalendarIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { CalendarDate, getLocalTimeZone, today } from "@internationalized/date";
import { Select } from "components/ui/select";
import { useIsTouchPrimary } from "hooks/use-is-touch-primary";
import * as React from "react";
import {
    DatePicker as AriaDatePicker,
    Button,
    Calendar,
    CalendarCell,
    CalendarGrid,
    CalendarMonthPicker,
    CalendarYearPicker,
    DateInput,
    DateSegment,
    Dialog,
    FieldError,
    Group,
    Label,
    Popover,
} from "react-aria-components";
import { dateValueToIso, isoToDateValue } from "./convert";

interface DatePickerProps {
    label: string | null;
    value: string | null;
    onChange: (value: string | null) => void;
    onOpenChange: (isOpen: boolean) => void;
    disabled: boolean;
    required: boolean;
    textSize: "sm" | "base";
    minValue: string | null;
    maxValue: string | null;
    visibleYears: number;
}

const maxSupportedIsoDate = "2999-12-31";

const fieldClasses =
    "form-input flex w-full items-center rounded-sm border-gray-300 hover:border-brand-600 focus-within:ring-2 focus-within:ring-brand-300 md:rounded data-[disabled]:cursor-default data-[disabled]:bg-gray-100";

const textSizeClass = (textSize: "sm" | "base"): string =>
    textSize === "sm" ? "text-sm" : "";

const NativeDatePicker: React.FC<DatePickerProps> = ({
    label,
    value,
    onChange,
    disabled,
    required,
    textSize,
    minValue,
    maxValue,
}) => (
    <div className="w-full">
        <label>
            {label ? (
                <div className={`mb-1 ${textSizeClass(textSize)}`}>{label}</div>
            ) : null}
            <input
                data-testid="date-picker-native"
                type="date"
                value={value ?? ""}
                disabled={disabled}
                required={required}
                min={minValue ?? undefined}
                max={maxValue ?? maxSupportedIsoDate}
                onChange={e => onChange(e.target.value ? e.target.value : null)}
                className={`form-input rounded-sm md:rounded w-full disabled:bg-gray-100 border-gray-300 hover:border-brand-600 active:border-brand-400 focus:ring-2 focus:ring-brand-300 ${textSizeClass(
                    textSize,
                )}`}
            />
        </label>
    </div>
);

const CalendarHeaderNav: React.FC<{ visibleYears: number }> = ({ visibleYears }) => (
    <header className="mb-2 flex items-center justify-between gap-2">
        <Button slot="previous" className="px-2 outline-none hover:text-brand-600">
            ◀
        </Button>
        <div className="flex flex-1 items-center gap-1">
            <CalendarMonthPicker>
                {({ value, onChange, items }) => (
                    <Select
                        data-testid="date-picker-month"
                        aria-label="Month"
                        textSize="sm"
                        value={String(value)}
                        onChange={e => onChange(Number(e.target.value))}>
                        {items.map(item => (
                            <option key={item.id} value={String(item.id)}>
                                {item.formatted}
                            </option>
                        ))}
                    </Select>
                )}
            </CalendarMonthPicker>
            <CalendarYearPicker visibleYears={visibleYears}>
                {({ value, onChange, items }) => (
                    <Select
                        data-testid="date-picker-year"
                        aria-label="Year"
                        textSize="sm"
                        value={String(value)}
                        onChange={e => onChange(Number(e.target.value))}>
                        {items.map(item => (
                            <option key={item.id} value={String(item.id)}>
                                {item.formatted}
                            </option>
                        ))}
                    </Select>
                )}
            </CalendarYearPicker>
        </div>
        <Button slot="next" className="px-2 outline-none hover:text-brand-600">
            ▶
        </Button>
    </header>
);

const isWithinRange = (
    value: CalendarDate,
    minValue: string | null,
    maxValue: string | null,
): boolean => {
    const min = isoToDateValue(minValue);
    const max = isoToDateValue(maxValue);
    return (
        (min === null || value.compare(min) >= 0) &&
        (max === null || value.compare(max) <= 0)
    );
};

interface TodayShortcutProps {
    onSelect: (iso: string) => void;
    minValue: string | null;
    maxValue: string | null;
}

const TodayShortcut: React.FC<TodayShortcutProps> = ({
    onSelect,
    minValue,
    maxValue,
}) => {
    const todayValue = today(getLocalTimeZone());
    return (
        <div className="mt-2 flex justify-center border-t border-gray-200 pt-2">
            <Button
                slot={null}
                data-testid="date-picker-today"
                isDisabled={!isWithinRange(todayValue, minValue, maxValue)}
                onPress={() => onSelect(todayValue.toString())}
                className="rounded px-3 py-1 text-sm font-medium text-brand-600 outline-none data-[disabled]:cursor-default data-[disabled]:text-gray-300 data-[hovered]:bg-brand-100">
                Today
            </Button>
        </div>
    );
};

const DesktopDatePicker: React.FC<DatePickerProps> = ({
    label,
    value,
    onChange,
    onOpenChange,
    disabled,
    required,
    textSize,
    minValue,
    maxValue,
    visibleYears,
}) => (
    <AriaDatePicker<CalendarDate>
        data-testid="date-picker"
        className="w-full"
        aria-label={label ? undefined : "Date"}
        value={isoToDateValue(value)}
        onChange={next => onChange(dateValueToIso(next))}
        onOpenChange={onOpenChange}
        minValue={isoToDateValue(minValue)}
        maxValue={isoToDateValue(maxValue)}
        isDisabled={disabled}
        isRequired={required}>
        {label ? (
            <Label className={`mb-1 block ${textSizeClass(textSize)}`}>{label}</Label>
        ) : null}
        <Group
            data-testid="date-picker-field"
            className={`${fieldClasses} ${textSizeClass(textSize)}`}>
            <DateInput className="flex flex-1">
                {segment => (
                    <DateSegment
                        segment={segment}
                        className="rounded px-0.5 tabular-nums outline-none focus:bg-brand-100 focus:text-brand-900"
                    />
                )}
            </DateInput>
            {value && !disabled && !required ? (
                <Button
                    slot={null}
                    data-testid="date-picker-clear"
                    onPress={() => onChange(null)}
                    className="px-1 text-gray-400 outline-none hover:text-brand-600">
                    <XMarkIcon className="h-4 w-4" />
                </Button>
            ) : null}
            <Button
                data-testid="date-picker-trigger"
                className="px-1 text-gray-500 outline-none hover:text-brand-600">
                <CalendarIcon className="h-5 w-5" />
            </Button>
        </Group>
        <FieldError
            data-testid="date-picker-error"
            className="mt-1 block text-sm text-red-600"
        />
        <Popover
            data-testid="date-picker-popover"
            isNonModal={true}
            className="z-1600 rounded border border-gray-200 bg-white p-3 shadow-lg outline-none">
            <Dialog className="outline-none">
                {({ close }) => (
                    <>
                        <Calendar>
                            <CalendarHeaderNav visibleYears={visibleYears} />
                            <CalendarGrid>
                                {date => (
                                    <CalendarCell
                                        date={date}
                                        className="flex h-8 w-8 cursor-pointer items-center justify-center rounded outline-none data-[disabled]:cursor-default data-[disabled]:text-gray-300 data-[hovered]:bg-brand-100 data-[selected]:bg-brand-600 data-[selected]:text-white"
                                    />
                                )}
                            </CalendarGrid>
                        </Calendar>
                        <TodayShortcut
                            minValue={minValue}
                            maxValue={maxValue}
                            onSelect={iso => {
                                onChange(iso);
                                close();
                            }}
                        />
                    </>
                )}
            </Dialog>
        </Popover>
    </AriaDatePicker>
);

export const DatePicker: React.FC<DatePickerProps> = props => {
    const isTouchPrimary = useIsTouchPrimary();
    return isTouchPrimary ? (
        <NativeDatePicker {...props} />
    ) : (
        <DesktopDatePicker {...props} />
    );
};
